import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import debug from 'debug';
import {tileCapMap, getDefaultTileOrder, getAuthTileData, getTiles, getLayoutsFromKeys, deleteHidden} from '../shared/utils/dashboardUtils';
import useSWR from 'swr';
import {GridPage} from '../../shared';
import {formatter} from '../../shared/utils/utils';
import LayoutSetup from '../shared/components/LayoutSetup';
import history from '../../history';
import axiosInstance from '../../shared/utils/axios';
import StickyHeaderBar from '../shared/components/StickyHeaderBar';
import {_openModal} from '../../shared/reducers/modals';

const log = debug('circleblack:investor:dashboard');

const Dashboard = ({context, container, session, openModal}) => {
  log('rendering');

  const [editing, setEditing] = React.useState(false);
  const [authorizedTiles, setAuthorizedTiles] = React.useState([]);
  const [currentTiles, setCurrentTiles] = React.useState([]);
  const [unselectedTiles, setUnselectedTiles] = React.useState([]);
  const [layoutSelectionKey, setLayoutSelectionKey] = React.useState();
  const [currentTilesLayoutData, setCurrentTilesLayoutData] = React.useState();
  const [currentLayouts, setCurrentLayouts] = React.useState();

  const {data: rawLayouts} = useSWR(context && `/user/layouts/investor-dashboard/${context.type}`);

  React.useEffect(() => {
    if (!rawLayouts || !authorizedTiles.length) {return;}

    if (rawLayouts?.layouts?.layoutKey) {
      setLayoutSelectionKey(rawLayouts.layouts.layoutKey);
      setCurrentTilesLayoutData(rawLayouts.layouts.order);
      return;
    }

    const displayedTileCap = (context.type === 'PORTFOLIO') ? 7 : 6;
    const numberOfDisplayedTiles = Math.min(displayedTileCap, authorizedTiles.length);

    if (!layoutSelectionKey) {
      const layoutTypeKey = numberOfDisplayedTiles + tileCapMap[context.type][numberOfDisplayedTiles];
      setLayoutSelectionKey(layoutTypeKey);
    }

    if (!currentTilesLayoutData) {
      setCurrentTilesLayoutData(getDefaultTileOrder(authorizedTiles, numberOfDisplayedTiles, context, session.preferences));
    }
  }, [rawLayouts, authorizedTiles]);

  let showMgp = false;
  const {data: advisors} = useSWR('/user/advisors');
  if (advisors?.filter(e => e.financialPlanning?.successPercent !== '?'
    && e.financialPlanning?.successPercent !== null).length > 0) {
    showMgp = true;
  }

  const probability = advisors && advisors[0]?.financialPlanning?.emoney?.successPercent;
  const eMoneyPoS = probability ? Number(probability.substring(0, probability.length - 1)) : null;
  const showRisk = !!(advisors || []).filter(v => !!v.risk).length;

  // TODO add dependencies as needed for getTiles()
  React.useEffect(() => {
    if (!session || !container) {return;}

    setAuthorizedTiles(getTiles({
      context,
      container,
      showPoS: showMgp || !!eMoneyPoS,
      showRisk: showRisk,
      openModal,
    }));
  }, [session, container, showMgp, eMoneyPoS, showRisk]);

  React.useEffect(() => {
    if (!currentTilesLayoutData) {return;}

    const currentKeys = Object.keys(currentTilesLayoutData);
    setCurrentTiles(authorizedTiles.filter(o => currentKeys?.includes(o.key)).map(o => o.jsx));
    setUnselectedTiles(authorizedTiles.filter(tile => !tile.hide && !currentKeys?.includes(tile.key)).map(tile => ({
      label: tile.title,
      action: tile.jsx?.props?.maximize,
    })));
  }, [authorizedTiles, currentTilesLayoutData]);

  React.useEffect(() =>{
    if (!layoutSelectionKey || !currentTilesLayoutData) {return;}

    setCurrentLayouts(getLayoutsFromKeys(layoutSelectionKey, currentTilesLayoutData));
  }, [layoutSelectionKey, currentTilesLayoutData]);

  // Removed as it causes no tiles to display if a tile is missing, not sure of use case.
  // const layoutKeysMatchTileKeys = (tiles, layouts) => {
  //   console.log(tiles, layouts)
  //   if (!tiles || !layouts) {return true;}
  //
  //   const currentTileKeys = lazy(tiles).pluck('key').toArray();
  //
  //   for (const val of Object.values(layouts)) {
  //     const currentLayoutKeys = lazy(val).pluck('i').toArray();
  //
  //     if ((currentTileKeys.length !== currentLayoutKeys.length) || !currentLayoutKeys.every(i => currentTileKeys.includes(i))) {
  //       return null;
  //     }
  //   }
  //
  //   return true;
  // };

  const stepOneTileData = deleteHidden(getAuthTileData(authorizedTiles, currentTilesLayoutData, context, session.preferences));

  const dropdownOptions = {
    mainSection: {
      options: [
        {
          label: 'Edit layout',
          action: ()=>{
            history.push(`${history.location.pathname}?context=${context.type.replace('ADVISOR_', '').toLowerCase()}`);
            setEditing(true);
          },
          isDisabled: !currentLayouts?.xl?.length && !currentTiles?.length,
        },
      ],
    },
    secondarySection: {
      label: 'Widgets',
      options: unselectedTiles,
    },
  };

  const headerButtons = {};

  if (container?.type === 'ACCOUNT') {
    headerButtons.additional = {
      label: 'All Accounts',
      action: () => {
        history.push('/accounts');
      },
    };
  }

  if (container?.type === 'GROUP') {
    headerButtons.additional = {
      label: 'All Groups',
      action: () => {
        history.push('/groups');
      },
    };
  }

  return (
    <>
      {!editing
          && <StickyHeaderBar
              title={container?.name ? `${container?.name}:` : 'Portfolio Total Value:'}
              subTitle={container?.balance?.amount
                ? formatter('currency', {noDecimals: true})(container?.balance?.amount) : '-'}
              buttons={headerButtons}
              dropdownOptions={dropdownOptions}/>}
      {!editing && currentLayouts?.xl?.length && !!currentTiles?.length && (
        <GridPage
          rowHeight={1}
          cols={{xl: (layoutSelectionKey === '11B') ? 5 : 24, lg: 24, md: 24, sm: 24, xs: 24}}
          editing={editing}
          layouts={currentLayouts}
          customPadding={[0, 0]}
          customMargin='0 0 10px 0'>
          {currentTiles}
        </GridPage>
      )}
      {editing && authorizedTiles && (
        <LayoutSetup tiles={{data: stepOneTileData}}
                     dismiss={() => {
                       history.push(history.location.pathname);
                       setEditing(false);
                     }}
                     context={context}
                     onSave={(selectionKey, layoutData) => {
                       setLayoutSelectionKey(selectionKey);
                       setCurrentTilesLayoutData(layoutData);
                       setEditing(false);

                       const layouts = {
                         layoutKey: selectionKey,
                         order: layoutData,
                       };
                       axiosInstance.post(`/user/layouts/investor-dashboard/${context.type}`, layouts);
                     }}
        />
      )}
    </>
  );
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  openModal: modal => dispatch(_openModal(modal)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);

Dashboard.propTypes = {
  session: PropTypes.object.isRequired,
  context: PropTypes.object,
  container: PropTypes.object,
  openModal: PropTypes.func.isRequired,
};
